import { useState } from 'react'
import { User, Mail, Phone, Bell, Shield, Globe } from 'lucide-react'
import { Button } from '@components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/ui/card'
import { Input } from '@components/ui/input'
import { LanguageSelect } from '@components/settings/LanguageSelect'
import { usePortalAuthStore } from '@hooks/usePortalAuthStore'
import { useToast } from '@hooks/use-toast'

export function PortalProfilePage() {
  const { toast } = useToast()
  const { user } = usePortalAuthStore()

  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    email_notifications: user?.email_notifications_enabled ?? true,
    sms_notifications: user?.sms_notifications_enabled ?? true,
  })

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    toast({
      title: 'Perfil actualizado',
      description: 'Los cambios de tu perfil fueron guardados.',
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Perfil</h1>
        <p className="text-muted-foreground mt-1">Gestiona la configuración de tu cuenta</p>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Información personal
            </CardTitle>
            <CardDescription>Actualiza tus datos de contacto</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Nombre</label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Apellido</label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email
                </label>
                <Input value={user?.email} disabled />
                <p className="text-xs text-muted-foreground">El correo no se puede cambiar</p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Teléfono
                </label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <Button type="submit">Guardar cambios</Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notificaciones
            </CardTitle>
            <CardDescription>Gestiona tus preferencias de notificación</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-muted">
              <div>
                <p className="font-medium">Notificaciones por correo</p>
                <p className="text-sm text-muted-foreground">Recibe recordatorios de citas por correo</p>
              </div>
              <input
                type="checkbox"
                checked={formData.email_notifications}
                onChange={(e) => setFormData({ ...formData, email_notifications: e.target.checked })}
                className="h-5 w-5"
              />
            </label>

            <label className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-muted">
              <div>
                <p className="font-medium">Notificaciones por SMS</p>
                <p className="text-sm text-muted-foreground">Recibe recordatorios por mensaje de texto</p>
              </div>
              <input
                type="checkbox"
                checked={formData.sms_notifications}
                onChange={(e) => setFormData({ ...formData, sms_notifications: e.target.checked })}
                className="h-5 w-5"
              />
            </label>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Idioma
            </CardTitle>
            <CardDescription>Elige el idioma de la interfaz</CardDescription>
          </CardHeader>
          <CardContent>
            <LanguageSelect className="w-48" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Seguridad
            </CardTitle>
            <CardDescription>Gestiona tu contraseña y ajustes de seguridad</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline">Cambiar contraseña</Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
